import type { PositionDto } from '@et/shared';
import { useState } from 'react';
import { api, describeError } from '../api/client';
import { dateTime, money, signed, time, tone } from '../lib/format';
import { t, useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import type { FeedEntry } from '../stores/live';
import { positionFigures } from '../lib/position-math';
import { ConfirmDialog } from './Dialog';
import { PositionChart } from './PositionChart';
import { Coin } from './CoinLogo';

/**
 * Open positions as cards: what went in, what it is worth now and what selling would leave
 * after both commissions. The chart opens under the card on demand.
 */
export function PositionList({ positions, onChanged }: { positions: PositionDto[]; onChanged?: () => void }): JSX.Element {
  const [closing, setClosing] = useState<PositionDto | null>(null);
  const [charted, setCharted] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tr = useT();
  if (positions.length === 0) return <div className="empty">{tr('No open positions.')}</div>;

  const close = async (p: PositionDto): Promise<void> => {
    setBusy(true);
    try {
      await api.post(`/positions/${p.tradeId}/close`);
      toast.success(t('Closing {symbol}', { symbol: p.symbol }));
      onChanged?.();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
      setClosing(null);
    }
  };

  const closingFigures = closing ? positionFigures(closing) : null;

  return (
    <div className="stack">
      {positions.map((p) => {
        const f = positionFigures(p);
        const asset = p.symbol.split('/')[0] ?? p.symbol;
        return (
          <div key={p.tradeId} className="position">
            <div className="position-head">
              <div>
                <b><Coin symbol={p.symbol} size={22} /></b>{' '}
                <span className="muted">· {tr(p.exitKey)} · {dateTime(p.openedAt)}</span>
              </div>
              <div className="row">
                <button className="small" onClick={() => { setCharted(charted === p.tradeId ? null : p.tradeId); }}>
                  {charted === p.tradeId ? tr('Hide chart') : tr('Chart')}
                </button>
                <button className="small" onClick={() => { setClosing(p); }}>{tr('Close')}</button>
              </div>
            </div>
            <dl className="figures">
              <div><dt>{tr('Bought for')}</dt><dd>{money(f.totalCost)} <span className="muted">USDT</span></dd><dd className="hint">{tr('{qty} {asset} @ {price}', { qty: p.qty, asset, price: money(p.avgEntry, 6) })}</dd></div>
              <div><dt>{tr('Worth now')}</dt><dd>{money(f.currentValue)} <span className="muted">USDT</span></dd><dd className="hint">{tr('price {price}', { price: money(p.lastPrice, 6) })}</dd></div>
              <div><dt>{tr('Net result (after fees)')}</dt><dd className={tone(f.netPnl)}><b>{signed(f.netPnl)}</b> USDT</dd><dd className={`hint ${tone(f.netPnlPct)}`}>{signed(f.netPnlPct)}%</dd></div>
              <div><dt>{tr('Binance commission')}</dt><dd>{money(p.feesPaid, 4)} <span className="muted">{tr('paid')}</span></dd><dd className="hint">{tr('+ {fee} on sale (est.)', { fee: money(f.exitFee, 4) })}</dd></div>
              <div><dt>{tr('Break-even price')}</dt><dd>{money(p.breakEven, 6)}</dd><dd className="hint">{tr('sell above this to profit')}</dd></div>
              <div><dt>{tr('Stop')}</dt><dd>{money(p.currentStop, 6)}</dd><dd className="hint">{p.currentTp ? tr('target {price}', { price: money(p.currentTp, 6) }) : tr('no fixed target')}</dd></div>
            </dl>
            {charted === p.tradeId && <PositionChart position={p} />}
          </div>
        );
      })}
      {closing && closingFigures && (
        <ConfirmDialog
          title={tr('Close {symbol}?', { symbol: closing.symbol })}
          confirmLabel={closingFigures.netPnl?.startsWith('-') ? tr('Sell at a loss') : tr('Sell with profit')}
          danger={closingFigures.netPnl?.startsWith('-') ?? false}
          busy={busy}
          onCancel={() => { setClosing(null); }}
          onConfirm={() => void close(closing)}
        >
          <p>{tr('Sells {qty} {asset} at market.', { qty: closing.qty, asset: closing.symbol.split('/')[0] ?? '' })}</p>
          <p>
            {tr('Estimated net result, after both commissions:')}{' '}
            <b className={tone(closingFigures.netPnl)}>{signed(closingFigures.netPnl)} USDT ({signed(closingFigures.netPnlPct)}%)</b>
          </p>
          <p className="hint">{tr('The final price can differ slightly from the one shown.')}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

export function Feed({ entries }: { entries: FeedEntry[] }): JSX.Element {
  const tr = useT();
  if (entries.length === 0) return <div className="empty">{tr('Waiting for events…')}</div>;
  return (
    <ul className="feed">
      {entries.map((e) => (
        <li key={e.id}>
          <span className="time">{time(e.ts)}</span>
          <span className={e.tone === 'neutral' ? '' : e.tone}>{e.text}</span>
        </li>
      ))}
    </ul>
  );
}
